/**
 * useNoIndex — small hook for utility/success/thin-content routes that
 * shouldn't be indexed by search engines. Mirrors the robots meta half
 * of useSeo without forcing callers to hand-author a title/description
 * they don't really have.
 *
 * Phase 4 policy: emits `noindex,follow` (not `nofollow`). The page is
 * excluded from the index but internal outbound links still pass PageRank
 * — useful for utility/thank-you/dispatcher pages that still link to
 * legitimate marketing pages.
 *
 * Callers that need to also block link-following (per-user token URLs,
 * admin auth pages) can pass `nofollow: true`.
 *
 * Preserves any prior <meta name="robots"> value on unmount so navigating
 * back to a public route restores the sitewide index policy.
 */
import { useEffect } from "react";

const DEFAULT_ROBOTS = "index,follow,max-image-preview:large";

export function useNoIndex(active = true, opts: { nofollow?: boolean } = {}) {
  const nofollow = opts.nofollow === true;
  useEffect(() => {
    if (!active) return;
    let el = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = el?.content ?? DEFAULT_ROBOTS;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.content = nofollow ? "noindex,nofollow" : "noindex,follow";
    return () => {
      if (el) el.content = prev || DEFAULT_ROBOTS;
    };
  }, [active, nofollow]);
}

